import { IPromotionRequest, IPromotionData } from 'typings/promotion';
import { action, observable, computed, makeObservable } from 'mobx';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { dataManagerFactory, cycleRules } from 'utils/MobxDataManager/singletons';
import { EventTypes, EntityTypes } from 'typings/enums';
import { Range } from 'nomst/Stores/Decision/range';
import { AmountCode } from 'typings/enums';
import { Amount } from 'nomst/Stores/Decision/amount';
import { requestNewTitleCode, requestNewTitleText } from 'common/constants';
import { EmployeeDispatcher } from './employee.dispatcher';
import { DataManagerFactory } from 'utils/MobxDataManager/dataManagerFactory';
import { compareItem } from 'common/compareItem';

export class PromotionRequest implements IPromotionRequest {

    jobCode: string;

    jobCodeKey: string;

    @observable proposedJobTitle: string;

    @observable proposedJobCode: string;

    @observable proposedJobCodeKey: string;

    @observable proposedPayRange: Nullable<Range>;

    @observable promotionValue: number;

    @observable managerSourceId: string;

    @observable promotionId: string;

    @observable promotionJobList: IJobSummary[];

    @observable currentComment: string;

    data: IPromotionData;

    isHourly: boolean;

    promotionAmount: Amount;

    employeeSourceId: string;

    isProcessing: boolean;

    shouldValidateForm: boolean;

    private _testDataManager: DataManagerFactory|undefined;

    private _configuration: Configuration;

    private _employeeDispatcher: EmployeeDispatcher;

    constructor(
        promotionDto: IPromotionRequest,
        configuration: Configuration,
        managerId: string,
        employeeDispatcher: EmployeeDispatcher,
        promotionAmount: NullOrUndefined<Amount>,
        testDataManager?: DataManagerFactory) {
        makeObservable(this);
        this.proposedJobCode = this.setJobCode(promotionDto);
        this.proposedJobCodeKey = promotionDto.jobCodeKey;
        this.proposedJobTitle = promotionDto.proposedJobTitle;
        this.promotionValue = promotionAmount?.value ?? 0;
        this.promotionId = promotionDto.promotionId;
        this.currentComment = '';
        this._configuration = configuration;
        this.managerSourceId = managerId;
        this.employeeSourceId = promotionDto.employeeSourceId;
        this._employeeDispatcher = employeeDispatcher;
        this._testDataManager = testDataManager;
    }

    @computed
    get promotionIdForComment(): string {
        return this.promotionId.split('-').join('');
    }

    @computed
    get compoundEntityIdForComment(): string {
        return `${this.employeeSourceId}-${this._configuration.cycleId}-${this.promotionIdForComment}`;
    }

    @action
    setPromotionAmount(amount: NullOrUndefined<Amount>): void {
        if (amount != null) {
            this.promotionAmount = amount;
        }
    }

    setJobCode(promotionDto: IPromotionRequest): string {
        // If jobcode is null but there is a proposed title then we should set the code to be new
        // title request
        return !promotionDto.jobCode && promotionDto.proposedJobTitle ?
            requestNewTitleCode : promotionDto.jobCode;
    }

    @action
    async setOriginalPromotionData(
        promotionDto: IPromotionRequest, promotionAmount: NullOrUndefined<Amount>): Promise<void> {
        this.data = {} as IPromotionData;
        if (promotionAmount == null || (!promotionDto.jobCode && !promotionDto.proposedJobTitle)) {
            return Promise.resolve();
        }

        this.data.jobCode = this.setJobCode(promotionDto);
        this.data.jobCodeKey = promotionDto.jobCodeKey;
        this.data.proposedJobTitle = promotionDto.proposedJobTitle;
        this.data.promotion = promotionAmount.value;
        await this.getPromotionJobList(promotionDto.jobCodeKey);
        this.calculatePayRange();
        if (!this.data.proposedJobTitle && this.proposedJobTitle) {
            this.data.proposedJobTitle = this.proposedJobTitle;
        }
    }

    get sortedPromotionJobsList(): IJobSummary[] {
        return this.sortPromotionJobList(this.promotionJobList);
    }

    sortPromotionJobList(jobList: IJobSummary[]): IJobSummary[] {
        return jobList.slice().sort((item1: IJobSummary, item2: IJobSummary) => {
            return compareItem(item1.ordinal, item2.ordinal);
        });
    }

    @action setPromotionJobList(promotionJobList: IJobSummary[]): void {
        this.promotionJobList = promotionJobList;
    }

    @action async getPromotionJobList(jobCodeKey: string): Promise<void> {
        if (this.promotionJobList && this.promotionJobList.length > 0 ) {
            return;
        }

        const jobDataManager = dataManagerFactory ?
            dataManagerFactory.getJobDataManager() : this._testDataManager?.getJobDataManager();
        const jobPromotionList = await jobDataManager?.getJobs(
            this._configuration.masterAccountId,
            this.managerSourceId,
            jobCodeKey
        );
        this.setPromotionJobList(jobPromotionList ?? []);
    }

    @action setComment(comment: string): void {
        this.currentComment = comment;
    }

    @action resetRequest(): void {
        this.setOriginalData();
        this.currentComment = '';
    }

    @action setOriginalData(): void {
        this.promotionAmount.setValue(this.data.promotion || 0);
        this.proposedJobCode = this.getStringValue(this.data.jobCode || requestNewTitleCode);
        this.proposedJobTitle = this.getStringValue(this.data.proposedJobTitle || '');
        this.proposedPayRange = null;
        if (this.proposedJobCode) {
            this.calculatePayRange();
        }

        if (this.proposedJobTitle) {
            this.proposedJobCode = requestNewTitleCode;
        }
    }

    calculatePayRange(): void {
        if (!this.promotionJobList) {
            return;
        }
        const selectedJob = this.promotionJobList.find(item => item.code === this.proposedJobCode);
        if (selectedJob) {
            this.proposedJobTitle = selectedJob.title;
            if (this.proposedJobCode !== selectedJob.code || !this.proposedPayRange) {
                this._employeeDispatcher.calculatePayRange(selectedJob);
            }
        }
    }

    @action async updatePromotionRequestDataEvent(): Promise<void> {
        const events = this.getPromotionRequestDataEvents();
        if (events.length === 0) {
            return Promise.resolve();
        }

        await dataManagerFactory.getPromotionRequestManager().updatePromotionRequestData(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            this.employeeSourceId,
            this.promotionId,
            events);

        this.data.jobCode = this.proposedJobCode;
        this.data.promotion = this.promotionAmount.value;
        this.data.proposedJobTitle = this.proposedJobTitle;
        this.promotionValue = this.promotionAmount.value;
    }

    @action async createPromotionRequestDataEvent(): Promise<void> {
        const promotionId = await dataManagerFactory.getPromotionRequestManager().createPromotionRequest(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            this.employeeSourceId);
        this.promotionId = promotionId ?? '';
    }

    @action
    handleSetFoundJob(job: IJobNoMst): void {
        this.proposedJobCode = '';
        this.proposedPayRange = null;
        if (!job) {
            return;
        }

        if (job.title === requestNewTitleText) {
            this.proposedJobCode = requestNewTitleCode;
            this.proposedJobTitle = '';
            return;
        }

        this.proposedJobCode = job.code;
        this.proposedJobCodeKey = job.jobCodeKey ?? '';

        this._employeeDispatcher.calculatePayRange(job);
    }

    handleChangeJob = (jobCode: string) => {
        this.proposedJobCode = jobCode;
        let selectedJob = null;
        if (jobCode === requestNewTitleCode) {
            selectedJob = this.getNewRequestTitleOption();
        } else if (jobCode === '') {
            this.proposedJobCode = jobCode;
            return;
        } else {
            selectedJob = this.promotionJobList.find(job => job.code === this.proposedJobCode);
            this.proposedJobTitle = selectedJob?.title ?? '';
        }

        this.handleSetFoundJob(selectedJob as IJobNoMst);
    }

    @action
    handleSetTypedJob(jobTitle: string): void {
        this.proposedJobCode = requestNewTitleCode;
        this.proposedJobTitle = jobTitle;
    }

    getNewRequestTitleOption(): IJobNoMst {
        return {
            code: requestNewTitleCode,
            jobCodeKey: null,
            title: requestNewTitleText,
            familyCode: '',
            ordinal: 0,
            minRange: 0,
            midRange: 0,
            maxRange: 0,
            currency: ''
        } as IJobNoMst;
    }

    async onCommentSent(): Promise<void> {
        await dataManagerFactory.getEventDataManager().sendPromotionCommentEvent(
            this._configuration.masterAccountId,
            this.employeeSourceId,
            this._configuration.cycleId,
            this.promotionIdForComment,
            {
                eventType: EventTypes.AddPromotionRequestConversationEvent,
                entityType: EntityTypes.PromotionRequest,
                data: {}
            });
    }

    async deletePromotionAmount(): Promise<void> {
        this.promotionAmount.setValue(0);
        const promotionAmountEvent = this.getPromotionAmountEvent();
        if (promotionAmountEvent) {
            await dataManagerFactory.getPromotionRequestManager().updatePromotionRequestData(
                this._configuration.masterAccountId,
                this._configuration.cycleId,
                this.employeeSourceId,
                this.promotionId,
                [promotionAmountEvent]);
        }
    }

    clearData(): void {
        this.data.jobCode = null;
        this.data.promotion = 0;
        this.data.proposedJobTitle = null;
        this.proposedJobCode = '';
        this.proposedPayRange = null;
        this.proposedJobTitle = '';
        this.promotionAmount.setValue(0);
    }

    getPromotionRequestDataEvents(): IEventNoMst[] {
        const events = [] as IEventNoMst[];
        if (this.data.jobCode !== this.proposedJobCode && this.proposedJobCode && this.proposedJobCode !== requestNewTitleCode) {
            events.push({
                eventType: EventTypes.UpdatePromotionJobEvent,
                entityType: EntityTypes.PromotionRequest,
                data: {
                    jobCode: this.proposedJobCode,
                    jobCodeKey: this.proposedJobCodeKey
                }
            });
        }

        if (this.data.proposedJobTitle !== this.proposedJobTitle && this.proposedJobTitle
                && (!this.proposedJobCode || this.proposedJobCode === requestNewTitleCode)) {
            events.push({
                eventType: EventTypes.UpdatePromotionRequestNewTitleEvent,
                entityType: EntityTypes.PromotionRequest,
                data: {
                    proposedJobTitle: this.proposedJobTitle,
                    jobCodeKey: this.proposedJobCodeKey
                }
            });
        }

        if (this.data.promotion !== this.promotionAmount.value) {
            const promotionAmountEvent = this.getPromotionAmountEvent();
            if (promotionAmountEvent) {
                events.push(promotionAmountEvent);
            }

            events.push({
                eventType: EventTypes.UpdatePromotionRequestAmountEvent,
                entityType: EntityTypes.PromotionRequest,
                data: {
                    originalValue: this.data.promotion,
                    value: this.promotionAmount.value
                }
            });
        }

        return events;
    }

    async saveComment(): Promise<void> {
        await dataManagerFactory.getConversationDataManager().saveMessage(
            this._configuration.masterAccountId,
            EntityTypes.PromotionRequest,
            this.compoundEntityIdForComment,
                {
                    message: this.currentComment
                } as IMessage);

        await this.onCommentSent();
    }

    getPromotionAmountEvent(): IEventNoMst|null {
        const amountCyclePromotion = this._configuration.getCycleBudgetField(AmountCode.Promotion);
        const affectWorkflow = !cycleRules.isCurrentCycleInPreviewMode;
        if (amountCyclePromotion) {
            return {
                eventType: EventTypes.UpdateAmountEvent,
                entityType: EntityTypes.EmployeeCycle,
                data: {
                    amountCycleId: amountCyclePromotion.budgetCycleId,
                    amountCode: AmountCode.Promotion,
                    originalValue: this.data.promotion,
                    value: this.promotionAmount.value
                },
                metadata: { affectWorkflow }
            };
        }

        return null;
    }

    payRangeDataNotAvailable(): boolean {
        return !!this.proposedJobCode
            && !this.proposedPayRange;
    }

    getStringValue(value: string): string {
        return value === null ? '' : value;
    }
}